import { addDays, addMonths, endOfDay, format, startOfDay } from 'date-fns';

export const DEFAULT_LOCALE = 'en-AU';

// Supported languages based on odin-translations (filtered to available primelocale files)
const supportedLocales = [
  'ja', // Japanese
  'zh-CN', // Chinese (Simplified) - Note: primelocale uses zh-CN instead of zh
  'zh-TW', // Chinese (Traditional)
  'fi', // Finnish
  'tl', // Filipino
  'hr', // Croatian
  'es', // Spanish
  'fr', // French
  'de', // German
  'it', // Italian
  'pt-BR', // Portuguese (Brazil)
  'pt', // Portuguese (Portugal)
  'hi', // Hindi
  'ar', // Arabic
  'ru', // Russian
  'ko', // Korean
  'bn', // Bengali
  'en', // English
  'en-GB', // English (United Kingdom)
  DEFAULT_LOCALE, // Always include default locale
];

// Vite-compatible loaders
const localeLoaders: Record<string, () => Promise<Record<string, unknown>>> = {
  ja: () => import('primelocale/js/ja.js'),
  'zh-CN': () => import('primelocale/js/zh_CN.js'),
  'zh-TW': () => import('primelocale/js/zh_TW.js'),
  fi: () => import('primelocale/js/fi.js'),
  tl: () => import('primelocale/js/tl.js'),
  hr: () => import('primelocale/js/hr.js'),
  es: () => import('primelocale/js/es.js'),
  fr: () => import('primelocale/js/fr.js'),
  de: () => import('primelocale/js/de.js'),
  it: () => import('primelocale/js/it.js'),
  'pt-BR': () => import('primelocale/js/pt_BR.js'),
  pt: () => import('primelocale/js/pt.js'),
  hi: () => import('primelocale/js/hi.js'),
  ar: () => import('primelocale/js/ar.js'),
  ru: () => import('primelocale/js/ru.js'),
  ko: () => import('primelocale/js/ko.js'),
  bn: () => import('primelocale/js/bn.js'),
  en: () => import('primelocale/js/en.js'),
  'en-GB': () => import('primelocale/js/en_GB.js'),
  [DEFAULT_LOCALE]: () => import('primelocale/js/en_AU.js'),
};

export const resolveLocale = async () => {
  try {
    const browserLocale = navigator?.language || DEFAULT_LOCALE;

    // Check if the browser locale is in our supported list
    let locale = browserLocale;
    if (!supportedLocales.includes(browserLocale)) {
      // Try to match language part only (e.g., 'en-US' -> 'en')
      const languageCode = browserLocale.split('-')[0];
      if (supportedLocales.includes(languageCode)) {
        locale = languageCode;
      } else {
        // Special case for Chinese: map 'zh' to 'zh-CN'
        if (languageCode === 'zh') {
          locale = 'zh-CN';
        } else {
          locale = DEFAULT_LOCALE;
        }
      }
    }

    // Use explicit loader for better Vite compatibility
    const loader = localeLoaders[locale] || localeLoaders[DEFAULT_LOCALE];
    const localeModule = await loader();
    const localeData = localeModule[locale.replace('-', '_')];
    return { locale, options: localeData };
  } catch {
    // Fallback to default locale if import fails
    const localeModule = await localeLoaders[DEFAULT_LOCALE]();
    const localeData = localeModule[DEFAULT_LOCALE.replace('-', '_')];
    return { locale: DEFAULT_LOCALE, options: localeData };
  }
};

type DatePartToken = 'day' | 'month' | 'year';

interface DatePattern {
  order: DatePartToken[];
  separator: string;
}

/**
 * Maps backend-style date formats (case-insensitive) to PrimeReact-compatible tokens.
 * PrimeReact renders and parses dates using these tokens (dd, mm, yy).
 * Backend formats often use 4-digit year (yyyy) and uppercase month (MM);
 * this map translates them to PrimeReact equivalents.
 */
const GENERIC_TO_PRIME_DATE_FORMAT: Record<string, string> = {
  'dd/mm/yyyy': 'dd/mm/yy',
  'mm/dd/yyyy': 'mm/dd/yy',
};

/**
 * Normalises common backend-style date formats to PrimeReact date format tokens.
 * Example: 'dd/MM/yyyy' → 'dd/mm/yy'
 * This ensures that the component's dateFormat prop (e.g. from backend JSON)
 * is understood by both PrimeReact (for display) and our text parser (for input handling).
 * Keeps unknown formats untouched so existing PrimeReact tokens still work.
 */
export const normalizeDateFormat = (dateFormat?: string): string | undefined => {
  if (!dateFormat) {
    return undefined;
  }

  const trimmed = dateFormat.trim();
  if (!trimmed) {
    return undefined;
  }

  return GENERIC_TO_PRIME_DATE_FORMAT[trimmed.toLowerCase()] ?? trimmed;
};

/**
 * Maps date-part tokens to their semantic meaning.
 * Supports both 1-char and 2-char tokens (e.g., 'd' and 'dd' both represent day).
 * This allows flexible parsing of various date-format patterns.
 */
const DATE_TOKEN_TO_PART: Record<string, DatePartToken> = {
  d: 'day',
  dd: 'day',
  m: 'month',
  mm: 'month',
  y: 'year',
  yy: 'year',
  yyyy: 'year',
};

/**
 * Extracts date token order and separator from a PrimeReact format string.
 * Example: 'dd/mm/yy' → { order: ['day', 'month', 'year'], separator: '/' }
 * Returns undefined if the format is invalid (missing tokens, duplicate parts, etc.).
 */

const parseDatePatternFromFormat = (dateFormat: string): DatePattern | undefined => {
  // PrimeReact format tokens are case-insensitive for our mapping purposes.
  const normalized = dateFormat.trim().toLowerCase();
  // Extract only date-part tokens in the order they appear in the format string.
  const tokens = normalized.match(/d+|m+|y+/g);
  if (!tokens || tokens.length !== 3) {
    return undefined;
  }

  // Map each token to its semantic part (day/month/year).
  const order = tokens.map((token) => DATE_TOKEN_TO_PART[token]);
  if (order.some((part) => !part)) {
    return undefined;
  }

  // Ensure all three parts are present (no duplicates).
  if (new Set(order).size !== 3) {
    return undefined;
  }

  // Keep the first non-token separator used by the supplied format.
  const separator = normalized.match(/[^dmy]+/)?.[0].trim() || '/';
  return { order, separator };
};

const parseDatePatternFromLocale = (locale: string): DatePattern => {
  // Fallback path: infer token order and separator from locale using Intl.DateTimeFormat.
  // This is used when no explicit dateFormat prop is provided or when it's invalid.
  // Example: 'en-AU' locale → { order: ['day', 'month', 'year'], separator: '/' }
  const parts = new Intl.DateTimeFormat(locale).formatToParts(new Date(2000, 0, 2));
  const order = parts.filter((p) => ['day', 'month', 'year'].includes(p.type)).map((p) => p.type as DatePartToken);
  const separator = parts.find((p) => p.type === 'literal')?.value.trim() || '/';
  return { order, separator };
};

/**
 * Parses a date string with a 2 or 4-digit year into a Date object.
 * Parsing strategy:
 * 1. Normalize backend-style dateFormat (e.g., 'dd/MM/yyyy' → 'dd/mm/yy')
 * 2. Use normalized format if valid; otherwise fall back to locale-based ordering
 * 3. Split input by detected separator and map values to day/month/year
 * 4. Construct Date using US format (month/day/year) to avoid ambiguity
 *
 * Examples:
 * - parseDateTime('12/03/2021', 'en', 'dd/MM/yyyy') → March 12, 2021
 * - parseDateTime('03/12/2021', 'en-AU', undefined) → December 3, 2021 (locale-driven)
 *
 * @throws on invalid input (missing parts, non-numeric values, etc.).
 */
export const parseDateTime = (text: string, locale: string, dateFormat?: string): Date => {
  // Normalize backend-style formats (e.g., 'dd/MM/yyyy') to PrimeReact tokens (e.g., 'dd/mm/yy').
  const normalizedDateFormat = normalizeDateFormat(dateFormat);
  // Extract pattern from normalized format if valid; otherwise use locale-based fallback.
  const datePattern = normalizedDateFormat ? parseDatePatternFromFormat(normalizedDateFormat) : undefined;
  // Priority: explicit normalized format > locale-based fallback for consistent parsing.
  const { order, separator } = datePattern ?? parseDatePatternFromLocale(locale);

  const values = text.split(separator);
  if (values.length !== 3) {
    throw new Error('Invalid date');
  }

  const map: Record<string, string> = {};
  order.forEach((type, i) => {
    map[type] = values[i];
  });

  // Construct Date using US format (month/day/year) to avoid locale ambiguity in parsing.
  const date = new Date(`${map.month}/${map.day}/${map.year}`);
  if (Number.isNaN(date.getTime())) {
    throw new Error('Invalid date');
  }

  return date;
};

const PRIME_TO_DATE_FNS_TOKEN: Record<string, string> = { d: 'd', dd: 'dd', m: 'M', mm: 'MM', y: 'yy', yy: 'yyyy' };

/** Formats a date the way the PrimeReact Calendar input shows it for the same `dd/mm/yy` style format. */
export const formatDisplayDate = (date: Date, primeDateFormat: string): string =>
  format(
    date,
    primeDateFormat.replace(/d+|m+|y+/g, (token) => PRIME_TO_DATE_FNS_TOKEN[token] ?? token)
  );

export type ValidationType =
  | 'DATE_BEFORE_OR_ON_TODAY'
  | 'DATE_ON_OR_AFTER_TODAY'
  | 'DATE_TODAY_PLUS_ONE_MONTH'
  | 'DATE_TODAY_PLUS_CUSTOM_NB_OF_DAYS';

export interface FieldValidation {
  validationType: ValidationType;
  numberOfDays: number;
}

export interface DateBounds {
  minDate: Date | undefined;
  maxDate: Date | undefined;
}

interface YearRangeInput {
  minDate?: Date;
  maxDate?: Date;
  value?: Date | Date[] | null;
  /** Override the default span around today (in years) when no bounds are set. */
  defaultSpan?: number;
}

export const DEFAULT_YEAR_SPAN = 100;

const collectYears = (value: Date | Date[] | null | undefined): number[] => {
  const candidates = Array.isArray(value) ? value : value ? [value] : [];
  return candidates
    .filter((d): d is Date => d instanceof Date && !Number.isNaN(d.getTime()))
    .map((d) => d.getFullYear());
};

/**
 * Builds the PrimeReact `yearRange` string ("yyyy:yyyy") for the calendar's year dropdown.
 * Widens the default ±span around today to always include minDate, maxDate, and any currently
 * selected value so the year is always reachable. PrimeReact further clips the visible options
 * by minDate/maxDate at render time, so the underlying range can safely be wider than the bounds.
 */
export const resolveYearRange = ({
  minDate,
  maxDate,
  value,
  defaultSpan = DEFAULT_YEAR_SPAN,
}: YearRangeInput): string => {
  const currentYear = new Date().getFullYear();
  const defaultStart = currentYear - defaultSpan;
  const defaultEnd = currentYear + defaultSpan;

  // Bounds and selected values must always sit inside the returned range so PrimeReact's
  // min/max clipping can never leave the dropdown without a reachable year.
  const includedYears = [...collectYears(minDate), ...collectYears(maxDate), ...collectYears(value)];
  const yearMin = Math.min(...includedYears);
  const yearMax = Math.max(...includedYears);

  const startYear = Math.min(defaultStart, yearMin);
  const endYear = Math.max(defaultEnd, yearMax);

  return `${startYear}:${endYear}`;
};

/** Converts a field validation rule into min/max date bounds for the calendar. */
export const resolveDateBounds = (fieldValidations: FieldValidation | undefined): DateBounds => {
  if (!fieldValidations) {
    return { minDate: undefined, maxDate: undefined };
  }

  switch (fieldValidations.validationType) {
    case 'DATE_BEFORE_OR_ON_TODAY':
      return { minDate: undefined, maxDate: endOfDay(new Date()) };
    case 'DATE_ON_OR_AFTER_TODAY':
      return { minDate: startOfDay(new Date()), maxDate: undefined };
    case 'DATE_TODAY_PLUS_ONE_MONTH':
      return { minDate: startOfDay(new Date()), maxDate: addMonths(new Date(), 1) };
    case 'DATE_TODAY_PLUS_CUSTOM_NB_OF_DAYS':
      if (!Number.isFinite(fieldValidations.numberOfDays)) {
        // biome-ignore lint/suspicious/noConsole: intentional warning for runtime diagnostics
        console.warn(
          `resolveDateBounds: DATE_TODAY_PLUS_CUSTOM_NB_OF_DAYS has invalid numberOfDays "${String(fieldValidations.numberOfDays)}"`
        );
        return { minDate: startOfDay(new Date()), maxDate: undefined };
      }
      return {
        minDate: startOfDay(new Date()),
        maxDate: addDays(new Date(), fieldValidations.numberOfDays),
      };
    default: {
      // Compile-time exhaustiveness check — will error if a new ValidationType is added without a case above
      const _exhaustiveCheck: never = fieldValidations.validationType;
      // Gracefully handle unknown or undefined validationType at runtime (e.g. misconfigured field data)
      // biome-ignore lint/suspicious/noConsole: intentional warning for runtime diagnostics
      console.warn(`resolveDateBounds: unrecognised validationType "${String(_exhaustiveCheck)}"`);
      return { minDate: undefined, maxDate: undefined };
    }
  }
};
